/**
 * RevenueCat mobile billing helper.
 *
 * RevenueCat is our broker for Apple IAP + Google Play Billing. It sends
 * a webhook to /api/webhook/revenuecat which flips is_premium and records
 * subscription_source.
 *
 * ⚠️ Configuration required:
 *   REACT_APP_REVENUECAT_APPLE_KEY   — Apple platform API key (starts app_)
 *   REACT_APP_REVENUECAT_GOOGLE_KEY  — Google platform API key (starts goog_)
 *
 * Product setup (identical id on both stores):
 *   product_id       = 'futureself_premium_monthly'
 *   entitlement_id   = 'premium'
 *
 * Web builds never load this module — Stripe remains the only checkout
 * path on the web.
 */

import { Capacitor } from '@capacitor/core';
import { isNativePlatform } from './native';

let _configured = false;
let _configuredUserId = null;

// Returns the module, never the plugin proxy (a Capacitor proxy is treated as a thenable and hangs).
const _lazyPurchases = () => import('@revenuecat/purchases-capacitor');

/** Initialise the SDK once, then switch the RevenueCat user when the signed-in account changes. */
export const configureRevenueCat = async (user) => {
  if (!isNativePlatform()) return false;
  const { Purchases } = await _lazyPurchases();
  if (_configured) {
    if (user?.user_id && user.user_id !== _configuredUserId) {
      await Purchases.logIn({ appUserID: user.user_id });
      _configuredUserId = user.user_id;
    }
    return true;
  }
  const platform = Capacitor.getPlatform();
  const apiKey = platform === 'ios'
    ? process.env.REACT_APP_REVENUECAT_APPLE_KEY
    : process.env.REACT_APP_REVENUECAT_GOOGLE_KEY;
  if (!apiKey) {
     
    console.warn('[revenuecat] API key not configured for platform', platform);
    return false;
  }
  await Purchases.configure({ apiKey, appUserID: user?.user_id });
  _configured = true;
  _configuredUserId = user?.user_id || null;
  return true;
};

/** On sign-out: detach purchases from the previous account. */
export const resetRevenueCatUser = async () => {
  if (!isNativePlatform() || !_configured) return;
  try {
    const { Purchases } = await _lazyPurchases();
    await Purchases.logOut();
  } catch (e) {
    console.warn('[revenuecat] logOut failed:', e?.message || e);
  }
  _configuredUserId = null;
};

/** Fetch the available offerings for the paywall. */
export const getOfferings = async () => {
  if (!isNativePlatform()) return null;
  try {
    const { Purchases } = await _lazyPurchases();
    const off = await Purchases.getOfferings();
    return off?.current || null;
  } catch (e) {
     
    console.warn('[revenuecat] getOfferings failed:', e?.message || e);
    return null;
  }
};

/** Present a package for purchase. Resolves to {isPremium, source}. */
export const purchasePackage = async (pkg) => {
  const { Purchases } = await _lazyPurchases();
  const result = await Purchases.purchasePackage({ aPackage: pkg });
  const entitlements = result?.customerInfo?.entitlements?.active || {};
  const active = entitlements['premium'];
  return {
    isPremium: !!active,
    source: Capacitor.getPlatform() === 'ios' ? 'apple_iap' : 'google_play',
    entitlement: active || null,
  };
};

/** Restore purchases (required by App Store guideline 3.1.1). */
export const restorePurchases = async () => {
  const { Purchases } = await _lazyPurchases();
  const info = await Purchases.restorePurchases();
  const active = info?.customerInfo?.entitlements?.active?.premium;
  return { isPremium: !!active };
};
